import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MagnifyingGlass } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { CATEGORY_LIST, AUDIENCE_LABEL } from '@/constants/categories';
import type { EventKind, Audience } from '@/types/database';
import { Pattern } from '@/components/patterns';
import { CatIcon } from '@/components/ui/CatIcon';
import { requestStartupNotifications } from '@/lib/appRuntime';

const STEPS = ['hello', 'city', 'kinds', 'audiences', 'permissions'] as const;
type Step = typeof STEPS[number];
type CityResult = {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    postcode?: string;
  };
};

function cityLabel(result: CityResult) {
  return result.address?.city
    ?? result.address?.town
    ?? result.address?.village
    ?? result.address?.municipality
    ?? result.display_name.split(',')[0].trim();
}

export default function Onboarding() {
  const { user, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('hello');
  const [city, setCity] = useState('');
  const [cityResults, setCityResults] = useState<CityResult[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityResult | null>(null);
  const [cityBusy, setCityBusy] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [kinds, setKinds] = useState<EventKind[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notifOptIn, setNotifOptIn] = useState<boolean | null>(null);

  async function searchCities() {
    if (!city.trim()) {
      setCityResults([]);
      setSelectedCity(null);
      setCityError('Entrez une ville pour lancer la recherche.');
      return [];
    }

    setCityBusy(true);
    setCityError(null);
    setSelectedCity(null);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=fr&limit=5&q=${encodeURIComponent(`${city}, France`)}`,
        { headers: { 'Accept-Language': 'fr' } },
      );
      const d = await r.json() as CityResult[];
      setCityResults(d);
      if (!d.length) setCityError('Aucune ville trouvée. Essayez avec un code postal ou un nom plus précis.');
      return d;
    } catch {
      setCityResults([]);
      setCityError('Recherche indisponible pour le moment. Réessayez dans quelques secondes.');
      return [];
    } finally {
      setCityBusy(false);
    }
  }

  async function next() {
    setSubmitError(null);
    if (step === 'city' && !selectedCity) {
      await searchCities();
      return;
    }
    const i = STEPS.indexOf(step);
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]); else finish();
  }

  function skipStep() {
    setSubmitError(null);
    setCityError(null);
    const i = STEPS.indexOf(step);
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]);
  }

  async function finish(nextNotifOptIn = notifOptIn ?? false) {
    if (!user) return;
    setLoading(true);
    setSubmitError(null);

    const lat = selectedCity ? parseFloat(selectedCity.lat) : null;
    const lon = selectedCity ? parseFloat(selectedCity.lon) : null;
    const { error } = await supabase.from('profiles').upsert({
      id: user.id,
      home_city: selectedCity ? cityLabel(selectedCity) : null,
      home_postcode: selectedCity?.address?.postcode ?? null,
      home_lat: lat,
      home_lon: lon,
      preferred_kinds: kinds.length ? kinds : null,
      preferred_audiences: audiences.length ? audiences : null,
      notifications_opt_in: nextNotifOptIn,
      onboarded_at: new Date().toISOString(),
      rgpd_consent_at: new Date().toISOString(),
    });
    if (error) {
      setSubmitError(error.message);
      setLoading(false);
      return;
    }
    await refreshProfile();
    setLoading(false);
    nav('/decouvrir', { replace: true });
  }

  async function handleActivateNotifications() {
    setLoading(true);
    try {
      await requestStartupNotifications(async (token) => {
        if (user) {
          await supabase.from('profiles').update({ push_token: token }).eq('id', user.id);
        }
      });
      setNotifOptIn(true);
      await finish(true);
    } catch {
      setNotifOptIn(false);
      await finish(false);
    }
    setLoading(false);
  }

  function handleSkipNotifications() {
    setNotifOptIn(false);
    void finish(false);
  }

  function toggle<T>(arr: T[], v: T): T[] { return arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]; }

  return (
    <div className="min-h-screen flex flex-col px-6 pt-[max(env(safe-area-inset-top),32px)] pb-[max(env(safe-area-inset-bottom),2rem)] max-w-sm mx-auto w-full">
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map(s => <span key={s} className={`h-0.5 flex-1 rounded ${STEPS.indexOf(s) <= STEPS.indexOf(step) ? 'bg-terre' : 'bg-paper-edge'}`} />)}
      </div>

      <div className="flex-1 flex flex-col">
        {step === 'hello' && (
          <>
            <span className="t-cap mb-3">N° I — Préface</span>
            <h1 className="t-ed-3xl mb-4">Bienvenue.<br/>Voici votre <em className="italic-keyword">carnet</em>.</h1>
            <p className="t-ui text-ink-2">Quelques questions pour vous proposer ce qui vous ressemble.</p>
          </>
        )}

        {step === 'city' && (
          <>
            <span className="t-cap mb-3">N° II — Votre quartier</span>
            <h1 className="t-ed-2xl mb-2">Vous êtes <em className="italic-keyword">d'où</em>, au juste ?</h1>
            <p className="t-ui text-ink-2 mb-6">On centre les sorties autour de chez vous.</p>
            <div className="flex gap-2">
              <Input
                autoFocus
                className="flex-1"
                placeholder="Clermont-l'Hérault, Bordeaux, Lille…"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setCityError(null);
                  setCityResults([]);
                  setSelectedCity(null);
                }}
                onKeyDown={e => e.key === 'Enter' && void searchCities()}
                label="Votre ville"
                error={cityError ?? undefined}
              />
              <Button
                type="button"
                className="self-end shrink-0"
                onClick={() => void searchCities()}
                loading={cityBusy}
                iconLeft={<MagnifyingGlass size={16} />}
              >
                Chercher
              </Button>
            </div>
            {selectedCity ? (
              <div className="mt-4 rounded-md bg-paper-elev ring-1 ring-paper-edge p-3">
                <div className="t-ed-md">{cityLabel(selectedCity)}</div>
                <div className="t-ui-xs text-ink-3 truncate">{selectedCity.display_name}</div>
              </div>
            ) : null}
            {cityResults.length > 0 ? (
              <ul className="mt-4 divide-y divide-paper-edge">
                {cityResults.map(result => (
                  <li key={`${result.display_name}-${result.lat}-${result.lon}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCity(result);
                        setCity(cityLabel(result));
                        setCityResults([]);
                        setCityError(null);
                      }}
                      className="w-full text-left py-3"
                    >
                      <div className="t-ed-md">{cityLabel(result)}</div>
                      <div className="t-ui-xs text-ink-3 truncate">{result.display_name}</div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}

        {step === 'kinds' && (
          <>
            <span className="t-cap mb-3">N° III — Vos goûts</span>
            <h1 className="t-ed-2xl mb-2">Qu'est-ce qui vous <em className="italic-keyword">tente</em> ?</h1>
            <p className="t-ui text-ink-2 mb-6">Au moins une, autant que vous voulez.</p>
            <div className="grid grid-cols-3 gap-3">
              {CATEGORY_LIST.map(c => {
                const on = kinds.includes(c.id);
                return (
                  <button key={c.id} onClick={() => setKinds(k => toggle(k, c.id))}
                    className={`relative aspect-[3/4] rounded-md overflow-hidden ring-1 transition ${on ? 'ring-terre ring-2' : 'ring-paper-edge'}`}
                    style={{ backgroundColor: `rgb(var(${c.colorVar}))`, color: 'rgb(var(--paper-elev))' }}>
                    <Pattern id={c.pattern} opacity={0.1} />
                    <div className="absolute inset-0 p-3 flex flex-col justify-between">
                      <CatIcon kind={c.iconKey} size={20} />
                      <span className="font-serif italic text-[15px] leading-tight">{c.word}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 'audiences' && (
          <>
            <span className="t-cap mb-3">N° IV — Pour qui</span>
            <h1 className="t-ed-2xl mb-2">Vous sortez <em className="italic-keyword">avec qui</em> ?</h1>
            <p className="t-ui text-ink-2 mb-6">On adapte les recommandations.</p>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(AUDIENCE_LABEL) as Audience[]).map(a => (
                <Chip key={a} active={audiences.includes(a)} onClick={() => setAudiences(arr => toggle(arr, a))}>{AUDIENCE_LABEL[a]}</Chip>
              ))}
            </div>
          </>
        )}

        {step === 'permissions' && (
          <>
            <span className="t-cap mb-3">N° V — Notifications</span>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-terre/10 mb-6">
              <Bell size={32} className="text-terre" weight="duotone" />
            </div>
            <h1 className="t-ed-2xl mb-4"><em className="italic-keyword">Dis-moi</em> quand ça bouge.</h1>
            <p className="t-ui text-ink-2 mb-8">
              Active les notifications pour recevoir la Boussole du jeudi soir et tes alertes personnalisées.
            </p>
            <div className="space-y-3">
              <Button variant="primary" full loading={loading} onClick={() => void handleActivateNotifications()}>
                Activer les notifications
              </Button>
              <button onClick={handleSkipNotifications} className="w-full text-center t-ui-sm text-ink-3 italic font-serif">
                Plus tard
              </button>
            </div>
          </>
        )}
      </div>

      {step !== 'permissions' && (
        <div className="pt-8 space-y-3">
          {submitError ? <p className="t-ui-sm text-danger">{submitError}</p> : null}
          <Button
            variant="primary"
            full
            onClick={() => void next()}
            loading={loading || cityBusy}
            disabled={step === 'city' && !city}
          >
            {step === 'city' && !selectedCity ? 'Chercher ma ville' : 'Continuer'}
          </Button>
          {step !== 'hello' && (
            <button onClick={skipStep} className="w-full text-center t-ui-sm text-ink-3 italic font-serif">Passer cette étape</button>
          )}
        </div>
      )}
    </div>
  );
}
